import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Check, Download, Zap, Shield, Crosshair, Server, Crown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_creator-hub-699/artifacts/znp5fjmw_image_7fc4ee3c-94ac-47f2-acda-a583cc0a2f45.png';

const products = [
  {
    id: 'foundation', name: 'FOUNDATION', price: 149, type: 'one_time', icon: Download, color: 'cyan',
    position: 'Entry point for builders who want structure',
    includes: [
      'A-Z Agent Build Doctrine (Core Edition)',
      'Auto-install file system (A-Z structure)',
      'Quick Start Guide',
      '"Where Am I?" navigation system',
      'Basic build checklist',
    ],
  },
  {
    id: 'system', name: 'SYSTEM', price: 397, type: 'one_time', icon: Zap, color: 'cyan',
    position: 'The complete build system',
    featured: true,
    includes: [
      'Full A-Z Agent Build Doctrine (Complete)',
      'Full folder system (A-Z + substructure)',
      'Architecture templates',
      'Frontend / backend starter frameworks',
      'Prompt library (build-focused)',
      'SOP templates',
      'Investor deck template',
      'Technical deck template',
      'Launch checklist',
      'Execution system',
    ],
  },
  {
    id: 'arsenal_kit', name: 'ARSENAL', price: 997, type: 'one_time', icon: Crosshair, color: 'magenta',
    position: 'For serious builders who want speed + leverage',
    includes: [
      'Everything in SYSTEM',
      'Advanced architecture blueprints',
      'Deployment guides',
      'System wiring examples',
      'Advanced workflows + orchestration',
      'Scalable system patterns',
      'UI component references',
      'Build acceleration frameworks',
    ],
  },
  {
    id: 'environment', name: 'ENVIRONMENT', price: 79, type: 'subscription', icon: Server, color: 'purple',
    position: 'Guided execution + support layer',
    includes: [
      'Login access',
      'Guided build system (step-by-step)',
      'SMITHERS-assisted workflow support',
      'Progress tracking',
      'System validation checkpoints',
      'Optional hosted build environment',
      'API integration support',
    ],
  },
  {
    id: 'genesis', name: 'GENESIS', price: 2500, type: 'custom', icon: Crown, color: 'magenta',
    position: 'Elite / private access',
    includes: [
      'Everything in ARSENAL',
      'Private environment',
      'Full system deployment support',
      'Dedicated infrastructure',
      'Advanced integration assistance',
    ],
  },
];

export default function DevKitPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loadingProduct, setLoadingProduct] = useState(null);

  const handlePurchase = async (productId) => {
    if (productId === 'genesis') return;
    if (!user) { navigate('/auth'); return; }
    setLoadingProduct(productId);
    try {
      const res = await axios.post(`${API}/checkout/devkit`, {
        product_id: productId,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setLoadingProduct(null);
    }
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="devkit-page">
      {/* Hero */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[--ch-cyan] rounded-full opacity-[0.02] blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[--ch-magenta] rounded-full opacity-[0.02] blur-[150px]" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[--ch-border] bg-[rgba(0,240,255,0.03)] mb-6 animate-fade-in-up">
            <img src={SHIELD_LOGO} alt="" className="w-4 h-4 object-contain" />
            <span className="font-label text-[--ch-text-secondary]">SMITHERS XI  /  A-Z AGENT BUILD SYSTEM</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] animate-fade-in-up stagger-1" style={{ fontFamily: 'Orbitron' }}  data-testid="devkit-title">
            <span className="text-white">Build a Real AI Agent</span><br />
            <span className="neon-cyan">The Right Way</span>
          </h1>
          <p className="mt-6 text-lg font-normal text-[--ch-text-secondary] max-w-2xl mx-auto animate-fade-in-up stagger-2" data-testid="devkit-subhead">
            A complete A-Z system that takes you from idea to architecture to deployment — without chaos.
          </p>

          <div className="flex justify-center gap-3 mt-8 animate-fade-in-up stagger-3">
            <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-neon px-8 py-4 rounded-full text-sm flex items-center gap-2" data-testid="devkit-cta-main">
              Get the System <ArrowRight size={16} />
            </button>
            <button onClick={() => document.getElementById('problem')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-outline-neon px-8 py-4 rounded-full text-sm flex items-center gap-2" data-testid="devkit-cta-secondary">
              See How It Works <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section id="problem" className="py-20 px-4 border-t border-[--ch-border]" data-testid="devkit-problem">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-8 text-center" >
            <span className="text-white">The Problem</span>
          </h2>
          <div className="ch-card p-8 text-center">
            <p className="text-lg font-normal text-[--ch-text-secondary] leading-relaxed">
              Right now, people are trying to build AI <span className="text-white font-semibold">in the middle</span>.
            </p>
            <div className="mt-6 space-y-3">
              {['They skip architecture.', 'They skip structure.', 'They guess.'].map((line, i) => (
                <p key={i} className="text-base text-[--ch-text-secondary] font-normal">{line}</p>
              ))}
            </div>
            <p className="mt-6 text-xl font-bold neon-magenta">And everything breaks.</p>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 px-4 border-t border-[--ch-border]" data-testid="devkit-solution">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-4 text-center" >
            <span className="neon-cyan">This system fixes that.</span>
          </h2>
          <p className="text-center text-sm text-[--ch-text-secondary] mb-10 font-normal">The A-Z Agent Build System gives you a clear path</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { letter: 'A', desc: 'Define the intelligence' },
              { letter: 'B', desc: 'Build the business' },
              { letter: 'C', desc: 'Create the narrative' },
              { letter: 'D', desc: 'Design and develop' },
              { letter: 'E', desc: 'Engineer and execute' },
              { letter: 'F', desc: 'Finalize' },
              { letter: 'G', desc: 'Launch' },
              { letter: 'H', desc: 'Scale' },
            ].map((step, i) => (
              <div key={i} className="ch-card p-4 text-center animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
                <span className="text-2xl font-black neon-cyan" >{step.letter}</span>
                <p className="text-xs text-[--ch-text-secondary] mt-1 font-normal">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 px-4 border-t border-[--ch-border]" data-testid="devkit-features">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-black mb-8" >
            <span className="text-white">What You Get</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              'Full A-Z Agent Build Doctrine',
              'Auto-installed file system',
              'Complete build path',
              'Templates, prompts, and checklists',
              'Execution framework',
            ].map((item, i) => (
              <div key={i} className="ch-card p-4 flex items-center gap-3 text-left">
                <Check size={16} className="text-[--ch-success] flex-shrink-0" />
                <span className="text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-[--ch-text-secondary] font-normal italic">
            This is not a course. This is the system for building AI agents the right way.
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 border-t border-[--ch-border]" data-testid="devkit-pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black" >
              <span className="text-white">Get The </span><span className="neon-cyan">System</span>
            </h2>
            <p className="text-sm text-[--ch-text-secondary] mt-2 font-normal">Own it. Open it. Start building.</p>
          </div>

          {/* Main 3 products row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {products.filter(p => p.type === 'one_time').map((product, i) => {
              const Icon = product.icon;
              return (
                <div key={product.id}
                  className={`ch-card p-6 flex flex-col animate-fade-in-up ${
                    product.featured ? 'neon-border-cyan' : 'hover:border-[rgba(0,240,255,0.3)]'
                  }`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                  data-testid={`devkit-${product.id}`}
                >
                  {product.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider relative"
                      style={{ background: 'linear-gradient(135deg, var(--ch-cyan), var(--ch-magenta))', color: '#000' }}>
                      Core Product
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                    product.color === 'cyan' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' :
                    'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]'
                  }`}>
                    <Icon size={20} />
                  </div>
                  <h3 className="text-sm font-black tracking-wider" >{product.name}</h3>
                  <p className="text-xs text-[--ch-text-secondary] mt-0.5 font-normal">{product.position}</p>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-black">${product.price}</span>
                    <span className="text-sm text-[--ch-text-secondary] font-normal ml-1">one-time</span>
                  </div>
                  <ul className="space-y-1.5 flex-1 mb-5">
                    {product.includes.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs font-normal text-[--ch-text-secondary]">
                        <Check size={12} className="text-[--ch-success] mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`buy-${product.id}`}
                    onClick={() => handlePurchase(product.id)}
                    disabled={loadingProduct === product.id}
                    className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-50 ${
                      product.featured ? 'text-black' : 'btn-outline-neon'
                    }`}
                    style={product.featured ? { background: 'linear-gradient(135deg, var(--ch-cyan), var(--ch-magenta))' } : {}}
                  >
                    {loadingProduct === product.id ? 'Loading...' : `Get ${product.name}`}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Environment + Genesis row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {products.filter(p => p.type !== 'one_time').map((product, i) => {
              const Icon = product.icon;
              return (
                <div key={product.id}
                  className="ch-card p-6 flex flex-col animate-fade-in-up"
                  style={{ animationDelay: `${(i + 3) * 0.1}s` }}
                  data-testid={`devkit-${product.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      product.color === 'purple' ? 'bg-[rgba(139,92,246,0.1)] text-[--ch-purple]' :
                      'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]'
                    }`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-black tracking-wider" >{product.name}</h3>
                      <p className="text-xs text-[--ch-text-secondary] font-normal">{product.position}</p>
                      <div className="mt-2">
                        {product.type === 'subscription' ? (
                          <>
                            <span className="text-2xl font-black">${product.price}</span>
                            <span className="text-sm text-[--ch-text-secondary] font-normal">/month</span>
                          </>
                        ) : (
                          <span className="text-xl font-bold text-[--ch-magenta]">$2,500+ / Custom</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-1.5 mb-5 flex-1">
                    {product.includes.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs font-normal text-[--ch-text-secondary]">
                        <Check size={12} className="text-[--ch-success] mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`buy-${product.id}`}
                    onClick={() => product.type === 'custom' ? null : handlePurchase(product.id)}
                    disabled={loadingProduct === product.id}
                    className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-50 ${
                      product.type === 'custom' ? 'bg-[--ch-purple] text-white' : 'btn-outline-neon'
                    }`}
                  >
                    {product.type === 'custom' ? 'Contact Sales' :
                     loadingProduct === product.id ? 'Loading...' : `Subscribe — $${product.price}/mo`}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Checkout copy */}
          <div className="mt-12 max-w-2xl mx-auto text-center ch-card p-8" data-testid="devkit-checkout-copy">
            <p className="text-sm text-[--ch-text-secondary] font-normal leading-relaxed">
              You are not buying information. You are getting <span className="text-white font-semibold">a complete system</span>.
              A proven framework used to build real AI agents from start to finish.
              Everything is included. Everything is ready. Everything is yours immediately.
            </p>
            <p className="mt-3 text-sm font-semibold text-white">Download it. Open it. Start building.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
